import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import FlagIcon from '@mui/icons-material/Flag';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import GridOnIcon from '@mui/icons-material/GridOn';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { AUTH_QUERY, GET_MY_SITES_QUERY, SMART_SEARCH_QUERY } from '../../gql';
import { useGoTo } from '../GoToContext/useGoTo';
import { SmartSearchSelection } from '../context/WorkspaceContext';
import { useWorkspace } from '../context/WorkspaceContext/useWorkspace';
import { TextField } from '../design-system';
import {
  addRecentSearchResult,
  clearRecentSearchResults,
  readRecentSearchResults,
} from './history';

const SEARCH_DEBOUNCE_MS = 300;
const POINT_ZOOM = 17;
const MAPY_LOGO_URL = 'https://api.mapy.com/img/api/logo.svg';
const MAPY_HOME_URL = 'https://mapy.com';
const MAPY_COPYRIGHT_URL = 'https://api.mapy.com/copyright';

const searchContainerStyles = { position: 'relative' };
const searchInputStyles = {
  width: '100%',
  minWidth: 0,
  '& .MuiInputBase-root': { fontSize: 14, pr: 0.75 },
  '& .MuiInputBase-input': {
    fontSize: 12,
    boxSizing: 'border-box',
  },
  '& .MuiInputAdornment-root': { mx: 0.5 },
};
const adornmentIconStyles = { fontSize: 15 };
const clearIconStyles = { fontSize: 14 };
const resultsStyles = {
  position: 'absolute',
  zIndex: 10,
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  maxHeight: 320,
  overflowY: 'auto',
  border: 1,
  borderColor: 'divider',
};
const resultButtonStyles = {
  width: '100%',
  display: 'block',
  textAlign: 'left',
  px: 1.25,
  py: 0.85,
  borderBottom: 1,
  borderColor: 'divider',
  '&:last-of-type': { borderBottom: 0 },
  '&:hover': { bgcolor: 'action.hover' },
};
const highlightedResultButtonStyles = {
  ...resultButtonStyles,
  bgcolor: 'action.selected',
};
const typeRowStyles = { minHeight: 14 };
const typeTextStyles = {
  color: 'text.secondary',
  fontSize: 10,
  lineHeight: 1.2,
};
const resultLabelStyles = { fontSize: 13, fontWeight: 500, lineHeight: 1.25 };
const emptyStyles = { px: 1.25, py: 1.5, color: 'text.secondary' };
const selectedStyles = {
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  px: 1.25,
  py: 1,
  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
};
const selectedTextStyles = { minWidth: 0 };
const coordinateTextStyles = {
  color: 'text.secondary',
  fontSize: 10,
  lineHeight: 1.25,
};
const closeButtonStyles = { p: 0.35, flexShrink: 0 };
const attributionStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  px: 1.25,
  py: 0.65,
  color: 'text.secondary',
  borderTop: 1,
  borderColor: 'divider',
};
const selectedAttributionStyles = {
  ...attributionStyles,
  px: 0,
  pt: 0.5,
  pb: 0,
  borderTop: 0,
};
const attributionLogoStyles = { display: 'block', height: 10, width: 'auto' };
const attributionLinkStyles = {
  color: 'text.secondary',
  fontSize: 9,
  lineHeight: 1,
  textDecoration: 'none',
};
const clearHistoryButtonStyles = {
  width: '100%',
  minHeight: 27,
  justifyContent: 'flex-start',
  gap: 0.65,
  px: 1.25,
  py: 0.35,
  color: 'text.secondary',
  borderTop: 1,
  borderColor: 'divider',
  '&:hover': { bgcolor: 'action.hover' },
};
const clearHistoryIconStyles = { fontSize: 13 };
const clearHistoryTextStyles = { fontSize: 10, lineHeight: 1.2 };

const typeMessages = defineMessages({
  COORDINATE: {
    id: 'map.search.type.coordinates',
    defaultMessage: 'Coordinates',
  },
  SITE: { id: 'map.search.type.site', defaultMessage: 'Site' },
  CADASTRAL_AREA: {
    id: 'map.search.type.cadastralArea',
    defaultMessage: 'Cadastral area',
  },
  MUNICIPALITY: {
    id: 'map.search.type.municipality',
    defaultMessage: 'Municipality',
  },
  VILLAGE: {
    id: 'map.search.type.village',
    defaultMessage: 'Municipality part',
  },
});

const typeIconStyles: Record<SmartSearchSelection['type'], object> = {
  COORDINATE: { fontSize: 12, color: '#ef5350' },
  SITE: { fontSize: 12, color: '#ffa726' },
  CADASTRAL_AREA: { fontSize: 12, color: '#7e57c2' },
  MUNICIPALITY: { fontSize: 12, color: '#42a5f5' },
  VILLAGE: { fontSize: 12, color: '#66bb6a' },
};

function isMapyResult(result: SmartSearchSelection): boolean {
  return result.id.startsWith('mapy:');
}

function preventSearchBlur(event: React.MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

interface SiteResultRow {
  id?: unknown;
  name?: unknown;
  shared?: unknown;
  longitude?: unknown;
  latitude?: unknown;
}

function buildRecentSiteResults(value: unknown): SmartSearchSelection[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((value) => value as SiteResultRow)
    .filter(
      (site) =>
        typeof site.id === 'string' &&
        typeof site.name === 'string' &&
        site.name.length > 0 &&
        typeof site.longitude === 'number' &&
        Number.isFinite(site.longitude) &&
        typeof site.latitude === 'number' &&
        Number.isFinite(site.latitude)
    )
    .slice(0, 5)
    .map((site) => ({
      id: `site:${site.id}`,
      type: 'SITE' as const,
      label: site.name as string,
      detail: site.shared ? 'Shared with me' : 'Owned by me',
      longitude: site.longitude as number,
      latitude: site.latitude as number,
      bounds: null,
      geometry: null,
      score: 0,
    }));
}

function MapyAttribution({ selected = false }: { selected?: boolean }) {
  const intl = useIntl();

  return (
    <Box sx={selected ? selectedAttributionStyles : attributionStyles}>
      <Box
        component="a"
        href={MAPY_HOME_URL}
        target="_blank"
        rel="noreferrer"
        aria-label={intl.formatMessage({
          id: 'map.search.mapy',
          defaultMessage: 'Search by Mapy.com',
        })}
        onMouseDown={preventSearchBlur}
      >
        <Box
          component="img"
          src={MAPY_LOGO_URL}
          alt="Mapy.com"
          sx={attributionLogoStyles}
        />
      </Box>
      <Box
        component="a"
        href={MAPY_COPYRIGHT_URL}
        target="_blank"
        rel="noreferrer"
        sx={attributionLinkStyles}
        onMouseDown={preventSearchBlur}
      >
        <FormattedMessage
          id="map.search.attributionOthers"
          defaultMessage="Seznam.cz a.s. and others"
        />
      </Box>
    </Box>
  );
}

function ResultType({ result }: { result: SmartSearchSelection }) {
  const intl = useIntl();
  const iconStyles = typeIconStyles[result.type];
  let icon: React.ReactNode;

  switch (result.type) {
    case 'COORDINATE':
      icon = <MyLocationIcon sx={iconStyles} />;
      break;
    case 'SITE':
      icon = <FlagIcon sx={iconStyles} />;
      break;
    case 'CADASTRAL_AREA':
      icon = <GridOnIcon sx={iconStyles} />;
      break;
    case 'MUNICIPALITY':
      icon = <AccountBalanceIcon sx={iconStyles} />;
      break;
    case 'VILLAGE':
      icon = <HolidayVillageIcon sx={iconStyles} />;
      break;
  }

  return (
    <Stack direction="row" alignItems="center" gap={0.5} sx={typeRowStyles}>
      {icon}
      <Typography sx={typeTextStyles}>
        {intl.formatMessage(typeMessages[result.type])} ·{' '}
        {result.detail === 'Shared with me'
          ? intl.formatMessage({
              id: 'map.search.sharedWithMe',
              defaultMessage: 'Shared with me',
            })
          : result.detail === 'Owned by me'
          ? intl.formatMessage({
              id: 'map.search.ownedByMe',
              defaultMessage: 'Owned by me',
            })
          : result.detail}
      </Typography>
    </Stack>
  );
}

interface SearchOptionProps {
  result: SmartSearchSelection;
  highlighted: boolean;
  onSelect: (result: SmartSearchSelection) => void;
}

const SearchOption = React.memo(
  ({ result, highlighted, onSelect }: SearchOptionProps) => {
    const handleMouseDown = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault(),
      []
    );
    const handleClick = useCallback(() => {
      onSelect(result);
    }, [onSelect, result]);

    return (
      <ButtonBase
        id={`smart-search-option-${result.id}`}
        role="option"
        aria-selected={highlighted}
        sx={highlighted ? highlightedResultButtonStyles : resultButtonStyles}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        <ResultType result={result} />
        <Typography noWrap sx={resultLabelStyles}>
          {result.label}
        </Typography>
      </ButtonBase>
    );
  }
);

function ClearRecentSearches({ onClear }: { onClear: () => void }) {
  return (
    <ButtonBase
      sx={clearHistoryButtonStyles}
      onMouseDown={preventSearchBlur}
      onClick={onClear}
    >
      <DeleteOutlineIcon sx={clearHistoryIconStyles} />
      <Typography sx={clearHistoryTextStyles}>
        <FormattedMessage
          id="map.search.clearHistory"
          defaultMessage="Clear last searches"
        />
      </Typography>
    </ButtonBase>
  );
}

function SelectedEntity({
  selection,
  onClear,
}: {
  selection: SmartSearchSelection;
  onClear: () => void;
}) {
  const intl = useIntl();

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      sx={selectedStyles}
    >
      <Stack sx={selectedTextStyles}>
        <ResultType result={selection} />
        <Typography noWrap sx={resultLabelStyles}>
          {selection.label}
        </Typography>
        <Typography sx={coordinateTextStyles}>
          {selection.latitude.toFixed(6)}, {selection.longitude.toFixed(6)}
        </Typography>
        {isMapyResult(selection) && <MapyAttribution selected />}
      </Stack>
      <IconButton
        title={intl.formatMessage({
          id: 'map.search.clearSelection',
          defaultMessage: 'Clear selected location',
        })}
        aria-label={intl.formatMessage({
          id: 'map.search.clearSelection',
          defaultMessage: 'Clear selected location',
        })}
        size="small"
        sx={closeButtonStyles}
        onClick={onClear}
      >
        <ClearIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

const SmartSearch = () => {
  const intl = useIntl();
  const [input, setInput] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [recentResults, setRecentResults] = useState<SmartSearchSelection[]>(
    []
  );
  const [historyReady, setHistoryReady] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { goTo, fitToBounds } = useGoTo();
  const {
    store: { smartSearchSelection },
    setSmartSearchSelection,
  } = useWorkspace();
  const { data: authData } = useQuery(AUTH_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const viewerId = authData?.viewer?.user?.id as string | undefined;

  useEffect(() => {
    if (!viewerId) {
      setRecentResults([]);
      setHistoryReady(false);
      return;
    }

    setRecentResults(readRecentSearchResults(viewerId));
    setHistoryReady(true);
  }, [viewerId]);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedQuery(input.trim()),
      SEARCH_DEBOUNCE_MS
    );

    return () => window.clearTimeout(timeout);
  }, [input]);

  const { data, loading, error } = useQuery(SMART_SEARCH_QUERY, {
    variables: { query: debouncedQuery },
    skip: debouncedQuery.length === 0,
    fetchPolicy: 'no-cache',
  });
  const searchResults = useMemo<SmartSearchSelection[]>(
    () => (input.trim() === debouncedQuery ? data?.smartSearch || [] : []),
    [data, debouncedQuery, input]
  );
  const emptyInput = input.trim().length === 0;
  const shouldLoadRecentSites =
    focused && historyReady && emptyInput && recentResults.length === 0;
  const { data: sitesData, loading: recentSitesLoading } = useQuery(
    GET_MY_SITES_QUERY,
    {
      skip: !shouldLoadRecentSites,
      fetchPolicy: 'cache-first',
    }
  );
  const recentSiteResults = useMemo(
    () => buildRecentSiteResults(sitesData?.getMySites),
    [sitesData]
  );
  const results = emptyInput
    ? recentResults.length > 0
      ? recentResults
      : recentSiteResults
    : searchResults;
  const hasMapyResults = results.some(isMapyResult);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [results]);

  const selectResult = useCallback(
    (result: SmartSearchSelection) => {
      if (viewerId) {
        setRecentResults((currentResults) =>
          addRecentSearchResult(viewerId, currentResults, result)
        );
      }

      setSmartSearchSelection(result);
      setFocused(false);
      inputRef.current?.blur();

      if (result.bounds) {
        fitToBounds(result.bounds);
      } else {
        goTo(result.longitude, result.latitude, POINT_ZOOM);
      }
    },
    [fitToBounds, goTo, setSmartSearchSelection, viewerId]
  );

  const handleClearSelection = useCallback(() => {
    setSmartSearchSelection(null);
  }, [setSmartSearchSelection]);

  const handleClearRecentSearches = useCallback(() => {
    if (viewerId) {
      clearRecentSearchResults(viewerId);
    }

    setRecentResults([]);
    setHighlightedIndex(0);
  }, [viewerId]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInput(event.target.value);
      setFocused(true);
    },
    []
  );

  const handleClearInput = useCallback(() => {
    setInput('');
    setDebouncedQuery('');
    inputRef.current?.focus();
  }, []);

  const handleFocus = useCallback(() => setFocused(true), []);
  const handleBlur = useCallback(() => setFocused(false), []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown' && results.length > 0) {
        event.preventDefault();
        setHighlightedIndex((current) => (current + 1) % results.length);
        return;
      }

      if (event.key === 'ArrowUp' && results.length > 0) {
        event.preventDefault();
        setHighlightedIndex(
          (current) => (current - 1 + results.length) % results.length
        );
        return;
      }

      if (event.key === 'Enter' && results[highlightedIndex]) {
        event.preventDefault();
        selectResult(results[highlightedIndex]);
        return;
      }

      if (event.key === 'Escape') {
        setFocused(false);
        inputRef.current?.blur();
      }
    },
    [highlightedIndex, results, selectResult]
  );

  const searching = !emptyInput && (input.trim() !== debouncedQuery || loading);
  const inputProps = useMemo(() => {
    const endAdornment = searching ? (
      <CircularProgress size={13} />
    ) : input ? (
      <IconButton
        aria-label={intl.formatMessage({
          id: 'map.search.clear',
          defaultMessage: 'Clear search',
        })}
        size="small"
        sx={closeButtonStyles}
        onClick={handleClearInput}
      >
        <ClearIcon sx={clearIconStyles} />
      </IconButton>
    ) : null;

    return {
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon sx={adornmentIconStyles} />
        </InputAdornment>
      ),
      endAdornment: (
        <InputAdornment position="end">{endAdornment}</InputAdornment>
      ),
    };
  }, [handleClearInput, input, intl, searching]);
  const showResults = focused && (!emptyInput || historyReady);
  const nativeInputProps = useMemo(
    () => ({
      role: 'combobox',
      'aria-label': intl.formatMessage({
        id: 'map.search.ariaLabel',
        defaultMessage: 'Smart map search',
      }),
      'aria-autocomplete': 'list' as const,
      'aria-expanded': showResults,
      'aria-controls': 'smart-search-results',
      'aria-activedescendant': results[highlightedIndex]
        ? `smart-search-option-${results[highlightedIndex].id}`
        : undefined,
    }),
    [highlightedIndex, intl, results, showResults]
  );

  return (
    <Stack gap={1}>
      <Box sx={searchContainerStyles}>
        <TextField
          inputRef={inputRef}
          surface="normal"
          placeholder={intl.formatMessage({
            id: 'map.search.placeholder',
            defaultMessage: 'Search places, sites or coordinates',
          })}
          value={input}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          sx={searchInputStyles}
          InputProps={inputProps}
          inputProps={nativeInputProps}
        />
        {showResults && (
          <Paper sx={resultsStyles}>
            <Box id="smart-search-results" role="listbox">
              {results.map((result, index) => (
                <SearchOption
                  key={result.id}
                  result={result}
                  highlighted={index === highlightedIndex}
                  onSelect={selectResult}
                />
              ))}
              {!emptyInput && !searching && !error && results.length === 0 && (
                <Typography sx={emptyStyles}>
                  <FormattedMessage
                    id="map.search.noMatch"
                    defaultMessage="No matching location"
                  />
                </Typography>
              )}
              {!emptyInput && error && (
                <Typography sx={emptyStyles}>
                  <FormattedMessage
                    id="map.search.unavailable"
                    defaultMessage="Search is unavailable"
                  />
                </Typography>
              )}
              {emptyInput && recentSitesLoading && (
                <Typography sx={emptyStyles}>
                  <FormattedMessage
                    id="map.search.loadingRecentSites"
                    defaultMessage="Loading recent Sites…"
                  />
                </Typography>
              )}
              {emptyInput && !recentSitesLoading && results.length === 0 && (
                <Typography sx={emptyStyles}>
                  <FormattedMessage
                    id="map.search.noRecent"
                    defaultMessage="No recent searches or Sites"
                  />
                </Typography>
              )}
            </Box>
            {emptyInput && recentResults.length > 0 && (
              <ClearRecentSearches onClear={handleClearRecentSearches} />
            )}
            {hasMapyResults && <MapyAttribution />}
          </Paper>
        )}
      </Box>
      {smartSearchSelection && (
        <SelectedEntity
          selection={smartSearchSelection}
          onClear={handleClearSelection}
        />
      )}
    </Stack>
  );
};

export { SmartSearch };
